/**
 * Mood Audio — ambient soundscapes on the unit's living-room amplifier.
 *
 * The library is HYBRID: the unit's /config/media/mood_audio folder decides which
 * sounds exist (surfaced through the folder sensor's file_list), while the
 * catalog below supplies display names and wheel order for the ones we ship.
 * A file with no catalog entry still appears, under a name derived from its
 * filename — so an operator can drop an .mp3 in without a client release.
 */

export interface MoodSound {
  /** File stem, e.g. 'rain'. This is the identifier sent to Home Assistant. */
  id: string;
  /** Display name shown in the wheel and (truncated) in the transport bar. */
  name: string;
}

/** Curated metadata for the sounds we ship. This order is the order in the wheel. */
export const MOOD_CATALOG: MoodSound[] = [
  { id: 'crackling_fire',   name: 'Crackling fire' },
  { id: 'ocean_waves',      name: 'Ocean waves' },
  { id: 'birds_chattering', name: 'Birds chattering' },
  { id: 'pink_noise',       name: 'Pink noise' },
  { id: 'rain',             name: 'Rain' },
  { id: 'theta_waves',      name: 'Theta waves' },
];

/**
 * Auto-stop duration in minutes. Every session the app can start is time-bounded.
 * (One pre-existing vOS path — mood_audio_yield_to_alarm — can still drop a
 * session's timer without stopping playback; see the note in mood_audio_start.)
 *
 * The indefinite option (0) was REMOVED 2026-09-09: a session with no end can only
 * be stopped from the app, so a resident who loses or reinstalls it is left with
 * audio looping until someone physically intervenes. Nothing on the unit was
 * time-bounding those sessions — the auto-stop timer is explicitly cancelled for
 * an indefinite one. Keep 0 out of this list; `mood_audio_start` on the unit also
 * clamps, so the two must not drift.
 *
 * 480 (8h) is the cap partly for its own sake and partly because
 * input_text.mood_audio_duration has max: 3 characters — there is no room above 999.
 */
export type MoodDuration = 5 | 15 | 30 | 60 | 120 | 240 | 480;
export const MOOD_DURATIONS: MoodDuration[] = [5, 15, 30, 60, 120, 240, 480];

/**
 * Mood-audio volume, as a percentage. The amplifier has ONE hardware volume —
 * there is no per-source gain — so mood audio borrows it for the length of a
 * session and the unit hands it back afterwards. These bounds mirror
 * input_number.mood_audio_volume on the unit and must stay in step with it.
 *
 * The floor is 1, not 0. Silencing the amplifier while the transport still reads
 * as playing is a confusing state, and releasing the hardware is the on/off
 * control's job — so 1% is offered as a real choice and 0% is not.
 *
 * Whole-percent steps, so every level in that range is reachable. Note the knock-on:
 * the helper carries no `initial:` (which would reset the resident's choice on every
 * HA restart), and HA falls back to a helper's MINIMUM when it has no restored
 * state — so a unit's very first session sits at 1% until the resident moves the
 * slider. Visible rather than silent, since the sheet shows the number.
 */
export const MOOD_VOLUME_MIN = 1;
export const MOOD_VOLUME_MAX = 100;
export const MOOD_VOLUME_STEP = 1;
export const DEFAULT_VOLUME = 40;

/**
 * Bring any reported or requested level onto the grid the unit will accept:
 * inside [MIN, MAX] and a whole percent (input_number rejects a value off its
 * step). A non-number becomes the default — the published attribute is a rendered
 * template, so it arrives as 'unknown' before the unit has ever stored a level.
 */
export function clampVolume(value: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) { return DEFAULT_VOLUME; }
  const snapped = Math.round(value / MOOD_VOLUME_STEP) * MOOD_VOLUME_STEP;
  return Math.min(MOOD_VOLUME_MAX, Math.max(MOOD_VOLUME_MIN, snapped));
}

/** Bar-only truncation cap, ellipsis included. The wheel shows full names. */
export const SOUND_NAME_MAX = 25;
export const DEFAULT_DURATION: MoodDuration = 30;

/**
 * Published template sensor: state playing|paused|idle, attributes sound + duration.
 *
 * Named "…_state" and NOT plain `sensor.mood_audio`, because the folder sensor below
 * already claims that id: HA's folder platform derives its name from the last path
 * component of the watched folder, so /config/media/mood_audio yields exactly
 * `sensor.mood_audio`. Two entities competing for one id means HA suffixes one `_2`
 * and this app reads whichever won — so they are kept deliberately distinct.
 */
export const MOOD_STATE_SENSOR = 'sensor.mood_audio_state';
/** The amplifier. Absent or unavailable => nothing to control. */
export const MOOD_AMP_ENTITY = 'media_player.livingspace';
/**
 * The HA `folder`-platform sensor over /config/media/mood_audio, whose `file_list`
 * attribute (a real list of absolute paths, from glob.glob) is the library.
 *
 * This id is DERIVED, not guessed. folder/sensor.py appends a trailing slash and then
 * double-splits — `os.path.split(os.path.split(path + "/")[0])[1]` — which returns the
 * last path component, so /config/media/mood_audio gives the name "mood_audio" and
 * hence `sensor.mood_audio`. Rename the folder and this MUST change with it.
 */
export const MOOD_LIBRARY_SENSOR = 'sensor.mood_audio';

/** '/config/media/mood_audio/crackling_fire.mp3' -> 'crackling_fire' */
export function stemFromPath(path: string): string {
  const base = (path ?? '').split('/').pop() ?? '';
  return base.replace(/\.mp3$/i, '');
}

/** 'pink_noise' -> 'Pink noise'. Sentence case, not title case. */
export function deriveName(stem: string): string {
  const words = (stem ?? '').replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * Combine the unit's files with the catalog:
 *  - in catalog AND on the unit  -> curated name, catalog position
 *  - in catalog, not on the unit -> omitted (never offer what can't play)
 *  - on the unit, not in catalog -> appended, derived name, alphabetical
 */
export function resolveLibrary(fileList: string[] | null | undefined): MoodSound[] {
  const stems = new Set(
    (fileList ?? [])
      .filter((p) => /\.mp3$/i.test(p))
      .map(stemFromPath)
      .filter((s) => s.length > 0),
  );
  const catalogIds = new Set(MOOD_CATALOG.map((s) => s.id));
  const known = MOOD_CATALOG.filter((s) => stems.has(s.id));
  const unknown = [...stems]
    .filter((s) => !catalogIds.has(s))
    .sort()
    .map((id) => ({ id, name: deriveName(id) }));
  return [...known, ...unknown];
}

/** Truncate to `max` characters INCLUDING the ellipsis. */
export function truncateSoundName(name: string, max: number = SOUND_NAME_MAX): string {
  const value = name ?? '';
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

export function durationLabel(d: MoodDuration): string {
  // "120 minutes" is accurate and reads badly on a wheel; past an hour, use hours.
  if (d < 60) { return `${d} minutes`; }
  const hours = d / 60;
  return hours === 1 ? '1 hour' : `${hours} hours`;
}
